import { useCallback, useRef } from 'react';
import {
  usePlaidLink,
  PlaidLinkOptionsWithLinkToken,
  PlaidLinkOnSuccess,
  PlaidLinkOnExit,
  PlaidLinkOnEvent,
} from 'react-plaid-link';

export type PlaidLinkMode = 'add_bank' | 'reconnect' | 'signup';

export type UsePlaidLinkWithLoggingOptions = PlaidLinkOptionsWithLinkToken & {
  mode: PlaidLinkMode;
  tenantId: string;
};

/**
 * Wraps usePlaidLink with structured logging for every Plaid event.
 * Logs link_session_id, institution_id, mode and tenantId on every callback.
 * The original onSuccess/onExit handlers are still called after logging.
 */
export function usePlaidLinkWithLogging({
  mode,
  tenantId,
  onSuccess: externalOnSuccess,
  onExit: externalOnExit,
  onEvent: externalOnEvent,
  ...rest
}: UsePlaidLinkWithLoggingOptions) {
  // Refs hold latest callback versions without invalidating the hook.
  const onSuccessRef = useRef(externalOnSuccess);
  onSuccessRef.current = externalOnSuccess;
  const onExitRef = useRef(externalOnExit);
  onExitRef.current = externalOnExit;
  const onEventRef = useRef(externalOnEvent);
  onEventRef.current = externalOnEvent;

  const onEvent: PlaidLinkOnEvent = useCallback(
    (eventName, metadata) => {
      console.log(`[PlaidLink:${mode}] event:${eventName}`, {
        mode,
        tenantId,
        link_session_id: metadata.link_session_id,
        institution_id: metadata.institution_id ?? null,
        institution_name: metadata.institution_name ?? null,
        error_type: metadata.error_type ?? null,
        error_code: metadata.error_code ?? null,
        error_message: metadata.error_message ?? null,
        view_name: metadata.view_name ?? null,
        timestamp: metadata.timestamp,
      });
      onEventRef.current?.(eventName, metadata);
    },
    [mode, tenantId],
  );

  const wrappedOnSuccess: PlaidLinkOnSuccess = useCallback(
    (publicToken, metadata) => {
      console.log(`[PlaidLink:${mode}] onSuccess`, {
        mode,
        tenantId,
        link_session_id: metadata.link_session_id,
        institution_id: metadata.institution?.institution_id ?? null,
        institution_name: metadata.institution?.name ?? null,
        account_count: metadata.accounts?.length ?? 0,
        public_token_prefix: publicToken?.slice(0, 20),
      });
      onSuccessRef.current(publicToken, metadata);
    },
    [mode, tenantId],
  );

  const wrappedOnExit: PlaidLinkOnExit = useCallback(
    (err, metadata) => {
      if (err) {
        console.error(`[PlaidLink:${mode}] onExit ERROR`, {
          mode,
          tenantId,
          link_session_id: metadata?.link_session_id ?? null,
          error_type: err.error_type,
          error_code: err.error_code,
          error_message: err.error_message,
          display_message: err.display_message ?? null,
          request_id: metadata?.request_id ?? null,
        });
      } else {
        console.log(`[PlaidLink:${mode}] onExit (closed)`, {
          mode,
          tenantId,
          link_session_id: metadata?.link_session_id ?? null,
          request_id: metadata?.request_id ?? null,
        });
      }
      onExitRef.current?.(err, metadata);
    },
    [mode, tenantId],
  );

  return usePlaidLink({
    ...rest,
    onSuccess: wrappedOnSuccess,
    onExit: wrappedOnExit,
    onEvent,
  });
}
